import { Input } from '@/components/ui/input';
import { ClubAddress } from '@/types/club.model';
import {
  Autocomplete,
  GoogleMap,
  LoadScriptNext,
  Marker,
} from '@react-google-maps/api';
import React, { useEffect, useRef, useState } from 'react';
import { getTimezone, parseGooglePlace } from './utils';

interface GoogleMapContainerProps {
  onAddressUpdate: (address: ClubAddress | null) => void;
  initial: string;
  isReadOnly?: boolean;
}

interface GoogleMapPosition {
  lat: number;
  lng: number;
}

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

const containerStyle = {
  width: '100%',
  height: '400px',
};

const center: GoogleMapPosition = {
  lat: -34.6037,
  lng: -58.3816,
};

const libraries: ('places' | 'geometry' | 'drawing' | 'visualization')[] = [
  'places',
];

export const GoogleMapContainer: React.FC<GoogleMapContainerProps> = ({
  onAddressUpdate,
  initial,
  isReadOnly = false,
}) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const [markerPosition, setMarkerPosition] =
    useState<GoogleMapPosition | null>(center);
  const autocompleteRef = useRef<HTMLInputElement>(null);

  const onPlaceChanged = async () => {
    if (isReadOnly || !autocomplete) return;
    
    const place = autocomplete.getPlace();
    if (place.geometry) {
      const location = place.geometry.location!;
      setMarkerPosition({ lat: location.lat(), lng: location.lng() });
      const timezone = await getTimezone(location.lat(), location.lng());
      const parsedPlace = parseGooglePlace(place);
      const placeWithTimezone = {
        ...parsedPlace,
        timezone: timezone ?? '',
      };
      onAddressUpdate(placeWithTimezone);
      map?.panTo(location);
    }
  };

  const onMapClick = async (event: google.maps.MapMouseEvent) => {
    if (isReadOnly || !event.latLng || !map) return;
    
    const geocoder = new google.maps.Geocoder();
    const location = { lat: event.latLng.lat(), lng: event.latLng.lng() };

    geocoder.geocode({ location }, async (results, status) => {
      if (status === 'OK' && results?.length) {
        const timezone = await getTimezone(location.lat, location.lng);
        const parsedPlace = parseGooglePlace(results[0]);
        const placeWithTimezone = {
          ...parsedPlace,
          timezone: timezone ?? ''
        };
        
        onAddressUpdate(placeWithTimezone);
        setMarkerPosition(location);
        if (autocompleteRef.current) {
          autocompleteRef.current.value = results[0].formatted_address;
        }
      }
    });
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isReadOnly) return;
    
    const { value } = event.target;
    const isValueEmpty = !value.trim();
    if (isValueEmpty) {
      onAddressUpdate(null);
      setMarkerPosition(null);
    }
  };

  const initialAddress = initial;

  useEffect(() => {
    if (!window.google || !window.google.maps) {
      return;
    }
    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ address: initialAddress }, (results, status) => {
      if (status === 'OK' && results && results[0].geometry.location) {
        const location = results[0].geometry.location;
        const position = { lat: location.lat(), lng: location.lng() };
        setMarkerPosition(position);

        if (map) map.panTo(position);
        if (autocompleteRef.current) {
          autocompleteRef.current.value = initialAddress;
        }
      }
    });
  }, [map, initialAddress]);

  const mapOptions = {
    disableDefaultUI: isReadOnly,
    gestureHandling: isReadOnly ? 'none' : 'auto',
    zoomControl: !isReadOnly,
    scrollwheel: !isReadOnly,
    disableDoubleClickZoom: isReadOnly,
    draggable: !isReadOnly,
  };

  return (
    <LoadScriptNext googleMapsApiKey={API_KEY} libraries={libraries}>
      <div>
        {isReadOnly ? (
          <Input
            type="text"
            value={initial || 'Sin dirección'}
            className="mb-5 w-full p-3 bg-gray-100 cursor-not-allowed"
            readOnly
          />
        ) : (
          <Autocomplete onLoad={setAutocomplete} onPlaceChanged={onPlaceChanged}>
            <Input
              ref={autocompleteRef}
              type="text"
              placeholder="Ingresa una dirección"
              className="mb-5 w-full p-3"
              onChange={onChange}
            />
          </Autocomplete>
        )}

        <div className={isReadOnly ? 'pointer-events-none opacity-90' : ''}>
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={markerPosition || center}
            zoom={14}
            onLoad={setMap}
            onClick={isReadOnly ? undefined : onMapClick}
            options={mapOptions}
          >
            {markerPosition && (
              <Marker
                key={`${markerPosition.lat}-${markerPosition.lng}`}
                position={markerPosition}
              />
            )}
          </GoogleMap>
        </div>

        {isReadOnly && (
          <p className="mt-2 text-xs text-gray-500 italic">
            📍 Ubicación del club (solo visualización)
          </p>
        )}
      </div>
    </LoadScriptNext>
  );
};